import { useCallback, useMemo } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useAccountAlias, useUserId } from "resources/account"
import { useNotifications } from "hooks/notifications"
import { useAnalytics } from "connected-components/analytics"
import { ShareDialog, type ShareDialogWording, recipientAnalyticsFields } from "connected-components/agent-chat/chatbox/share-dialog"
import type { DirectoryEntry, ShareRecipient } from "connected-components/agent-chat/chatbox/resource-sharing"
import type { SkillCatalogSkill } from "connected-components/skills/types"
import { SKILL_EVENTS } from "connected-components/skills/analytics/events"
import { getSharedSkillsQueryKey } from "resources/skills"

export interface SkillShareDialogProps {
  open: boolean
  onClose: () => void
  skill: SkillCatalogSkill
  resourceId: string
  mode?: "share" | "manage"
}

const SKILL_SHARE_WORDING: ShareDialogWording = {
  title: "Share skill",
  accessNote: "Everyone you add can find this skill and switch it on for Kelly. They can't edit or re-share it.",
  shareFailure: "Couldn't share this skill. Try again.",
}

const SKILL_MANAGE_WORDING: ShareDialogWording = { ...SKILL_SHARE_WORDING, title: "Manage sharing" }

export function SkillShareDialog({ open, onClose, skill, resourceId, mode = "share" }: SkillShareDialogProps) {
  const analytics = useAnalytics()
  const notifications = useNotifications()
  const accountAlias = useAccountAlias()
  const userId = useUserId()
  const queryClient = useQueryClient()

  const resolveResourceId = useCallback(async () => resourceId, [resourceId])

  const target = useMemo(() => ({
    resourceId,
    isLoading: false,
    isError: false,
    isCreating: false,
    resolveResourceId,
  }), [resourceId, resolveResourceId])

  const confirmation = useCallback((recipients: DirectoryEntry[]) => ({
    title: `Share "${skill.name}"?`,
    body: `${new Intl.ListFormat("en", { type: "conjunction" }).format(recipients.map((entry) => entry.name))} will be able to find "${skill.name}" and switch it on for Kelly. You can stop sharing later.`,
  }), [skill.name])

  const onShared = useCallback((_resourceId: string, granted: DirectoryEntry[]) => {
    analytics.track(SKILL_EVENTS.SKILL_SHARED, {
      skillId: skill.id,
      ...recipientAnalyticsFields(granted),
    })
  }, [analytics, skill.id])

  const onUnshared = useCallback((_resourceId: string, recipient: ShareRecipient) => {
    analytics.track(SKILL_EVENTS.SKILL_UNSHARED, {
      skillId: skill.id,
      recipientKind: recipient.kind,
      recipientId: recipient.id,
    })
    if (mode === "manage") {
      void queryClient.invalidateQueries({ queryKey: getSharedSkillsQueryKey(accountAlias, userId) })
      notifications.success(`${recipient.name} no longer has ${skill.name}.`)
    }
  }, [analytics, skill.id, skill.name, mode, queryClient, accountAlias, userId, notifications])

  return (
    <ShareDialog
      open={open}
      onClose={onClose}
      resourceType="SKILL"
      resourceName={skill.name}
      target={target}
      wording={mode === "manage" ? SKILL_MANAGE_WORDING : SKILL_SHARE_WORDING}
      idPrefix="skill-share"
      allowAdding={mode !== "manage"}
      confirmation={confirmation}
      onShared={onShared}
      onUnshared={onUnshared}
    />
  )
}
